<template>
  <modal
    elem-id="global-search-modal"
    :show="isVisible"
    :title="t('Buscar no sistema')"
    size="large"
    :close-label="t('Fechar')"
    :save-fn="null"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <input-qrcode
        ref="qrcodeRef"
        v-model="query"
        elem-id="global-search"
        autofocus
        :placeholder="t('Cliente (nome, documento, telefone), orçamento, compromisso, ordem, bairro, localização, serviço ou QR code')"
        :camera-title="t('Aponte para o QR Code do item')"
        solid-background
        data-testid="global-search-qrcode"
        @input="handleInput"
      />

      <div v-if="isLoading" class="global-search-state" data-testid="global-search-loading">
        <i class="pi pi-spin pi-spinner" />
        <span>{{ t('Buscando...') }}</span>
      </div>

      <div v-else-if="error" class="global-search-state global-search-error" data-testid="global-search-error">
        <i class="pi pi-exclamation-triangle" />
        <span>{{ error }}</span>
      </div>

      <div
        v-else-if="results.length === 0 && query.trim().length >= GLOBAL_SEARCH_MIN_TERM_LENGTH"
        class="global-search-state"
        data-testid="global-search-empty"
      >
        <i class="pi pi-search" />
        <span>{{ t('Nada encontrado') }}</span>
      </div>

      <div
        v-else-if="query.trim().length < GLOBAL_SEARCH_MIN_TERM_LENGTH"
        class="global-search-state"
        data-testid="global-search-hint"
      >
        <i class="pi pi-info-circle" />
        <span>{{ t('Digite ao menos 2 caracteres para buscar') }}</span>
      </div>

      <div v-else class="global-search-results">
        <button
          v-for="row in results"
          :key="`${row.tipo}-${row.id}-${row.matched_item_id ?? ''}`"
          class="global-search-result"
          :data-testid="`global-search-result-${row.tipo}-${row.id}`"
          @click="handleSelect(row)"
        >
          <span class="global-search-type" :class="`global-search-type--${row.tipo}`" :data-testid="`global-search-type-${row.tipo}-${row.id}`">
            {{ tipoLabel(row.tipo) }}
          </span>
          <span class="global-search-title">{{ row.titulo }}</span>
          <Tag
            v-if="row.status"
            :value="statusOf(row).label"
            :severity="statusOf(row).severity"
            :data-testid="`global-search-status-${row.tipo}-${row.id}`"
          />
          <span v-if="row.matched_field" class="global-search-badge">
            {{ matchedFieldLabel(row.matched_field) }}<template v-if="row.matched_value && row.matched_field !== 'nome' && row.matched_field !== 'cliente'">: {{ row.matched_value }}</template>
          </span>
          <span v-if="row.franquia" class="global-search-franchise">{{ row.franquia }}</span>
          <span v-if="row.subtitulo" class="global-search-subtitle">{{ row.subtitulo }}</span>
        </button>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useGlobalSearch, GLOBAL_SEARCH_MIN_TERM_LENGTH } from '../../composables/useGlobalSearch';
import { useSOStatusMap } from '../../composables/useSOStatusMap';
import type { Busca_GeralRow } from '../../contracts/Busca_GeralRow';

const { t } = useI18n();
const router = useRouter();
const { results, isLoading, error, search, clear } = useGlobalSearch();
const { getOrderStatus } = useSOStatusMap();

const qrcodeRef = ref<{ focus: () => void } | null>(null);
const isVisible = ref<boolean>(false);
const query = ref<string>('');

type Severity = 'success' | 'info' | 'warn' | 'danger' | 'secondary';

const tipoLabel = (tipo: string): string => {
  switch (tipo) {
    case 'cliente': return t('Cliente');
    case 'orcamento': return t('Orçamento');
    case 'compromisso': return t('Compromisso');
    case 'ordem': return t('Ordem de serviço');
    default: return tipo;
  }
};

/**
 * Orçamento/compromisso/cliente statuses arrive already translated by the
 * backend; the SO status is a code the SPA owns (useSOStatusMap).
 */
const statusOf = (row: Busca_GeralRow): { label: string; severity: Severity } => {
  if (row.tipo === 'ordem') {
    const s = getOrderStatus(row.status_raw ?? row.status ?? '');
    return { label: s.label, severity: s.severity as Severity };
  }
  const raw = (row.status_raw ?? '').toLowerCase();
  const label = row.status ?? '';
  if (raw === 'cancelado' || raw === '0' || raw === '2') return { label, severity: raw === '2' ? 'secondary' : 'danger' };
  if (raw === 'aprovado' || raw === 'realizado' || raw === '1' || raw === 'todos os serviços realizados') return { label, severity: 'success' };
  if (raw === 'pendente' || raw === 'agendado' || raw === 'compromisso agendado') return { label, severity: 'warn' };
  return { label, severity: 'info' };
};

const matchedFieldLabel = (field: string): string => {
  switch (field) {
    case 'nome': case 'cliente': case 'customer': return t('Cliente');
    case 'documento': return t('Documento');
    case 'telefone': return t('Telefone');
    case 'id': case 'order_id': return t('Número');
    case 'bairro': return t('Bairro');
    case 'localizacao': return t('Localização');
    case 'servico': return t('Serviço');
    case 'titulo': return t('Título');
    case 'item_id': return t('Item');
    case 'attribute': return t('Atributo');
    case 'qrcode': return t('QR Code');
    default: return field;
  }
};

const handleInput = (): void => {
  search(query.value);
};

const handleSelect = (row: Busca_GeralRow): void => {
  log('GlobalSearchModal', 'handleSelect', 'Result selected', row);
  switch (row.tipo) {
    case 'cliente':
      eventBus.emit('modal.cliente.open', { id: Number(row.id) });
      break;
    case 'orcamento':
      eventBus.emit('modal.orcamentos.open', { id: Number(row.id) });
      break;
    case 'compromisso':
      eventBus.emit('modal.gestao-compromisso.open', { id: Number(row.id) });
      break;
    case 'ordem':
      if (row.matched_item_id && (row.matched_field === 'item_id' || row.matched_field === 'attribute' || row.matched_field === 'qrcode')) {
        eventBus.emit('modal.so-order-item.open', { itemId: Number(row.matched_item_id) });
      } else {
        router.push('/pdv/checkout/' + row.id);
      }
      break;
  }
  handleClose();
};

const handleOpen = async (): Promise<void> => {
  log('GlobalSearchModal', 'handleOpen', 'Opening');
  isVisible.value = true;
  await nextTick();
  await nextTick();
  qrcodeRef.value?.focus();
};

const handleClose = (): void => {
  log('GlobalSearchModal', 'handleClose', 'Closing');
  isVisible.value = false;
  query.value = '';
  clear();
};

onMounted(() => {
  eventBus.on('modal.global-search.open', handleOpen);
  eventBus.on('modal.global-search.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off('modal.global-search.open', handleOpen);
  eventBus.off('modal.global-search.close', handleClose);
});
</script>

<style scoped>
.global-search-state { display: flex; align-items: center; gap: 8px; padding: 24px 12px; justify-content: center; color: var(--p-text-muted-color); font-size: 13px; }
.global-search-error { color: var(--p-red-500); }
.global-search-results { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; }
.global-search-result {
  display: flex; align-items: center; flex-wrap: wrap; gap: 10px; padding: 10px 14px;
  background: var(--p-surface-50); border: 1px solid var(--p-surface-border); border-radius: 6px;
  cursor: pointer; text-align: left; transition: background 0.15s, border-color 0.15s; width: 100%;
}
.global-search-result:hover { background: var(--p-surface-100); border-color: var(--p-primary-color); }
.global-search-type { font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 8px; color: #fff; flex-shrink: 0; text-transform: uppercase; letter-spacing: 0.02em; }
.global-search-type--cliente { background: var(--p-blue-500); }
.global-search-type--orcamento { background: var(--p-purple-500); }
.global-search-type--compromisso { background: var(--p-teal-500); }
.global-search-type--ordem { background: var(--p-orange-500); }
.global-search-title { font-weight: 600; font-size: 13px; color: var(--p-text-color); }
.global-search-badge { font-size: 11px; padding: 2px 8px; border-radius: 8px; background: var(--p-surface-200); color: var(--p-text-color); }
.global-search-franchise { font-size: 11px; color: var(--p-text-muted-color); margin-left: auto; }
.global-search-subtitle { flex-basis: 100%; font-size: 12px; color: var(--p-text-muted-color); }
</style>
